import React from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider, MobileTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import moment from 'moment';
import {FormHelperText, InputAdornment} from '@mui/material';
import {
    CustomStyleDatePicker,
    CustomStyleDatePickerRequired,
    CustomStyleFormHelperText, CustomStyleTimePickerPopper
} from './Styles';
import CustomDateTimePickerApp from "./CustomDateTimePicker/CustomDateTimePickerApp";
import {DATE_TIME_PICKER_MODEL} from "./CustomDateRangePicker/Model";
import Cookies from "js-cookie";
import {dateInputLanguage, muiLocaleText} from "../hooks/dateInputLanguage";
import {TimePickerChip} from "./buttons/IconButtons";

/**
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 * @Description Time picker component
 * @param String id: Time picker identifier (required).
 * @param String title: Title of input (required).
 * @param String name: Name of the model that receives the input (required).
 * @param Date value: Value del component (required).
 * @param event handleChange: Event to update the component value (required).
 * @param Boolean disabled: Variable to disable the input (required).
 * @param Boolean emptyFields: Indicates if the field is an empty field error (required).
 * @param Boolean required: Variable to indicate if it is required (required).
 * @param Boolean ampm: Indicates whether to display the time in 24-hour format or not (required).
 * @param String inputFormat: format input  HH:mm:ss (optional).
 * @param Boolean formatValue: If true, it will indicate an output in the string format HH:mm:ss (optional).
 * @param Boolean showOkButton: Show or hide the button (optional).
 * @returns {JSX.Element}
 */
const CustomTimePicker = ({
    id = 'tp_ctp_time',
    title,
    name,
    value,
    handleChange,
    disabled = false,
    emptyFields = false,
    required = false,
    ampm = true,
    inputFormat = 'HH:mm:ss',
    formatValue = true,
    showOkButton = true,
    openTo = 'hours',
    isValid=true,
    isValidText="date_invalid",
    helperText,
    minTime=undefined,
    maxTime=undefined,
    views = ['hours', 'minutes', 'seconds']
}) => {
    const { t } = useTranslation();
    const lCode = Cookies.get('i18next') || 'en';
    const { i18n } = useTranslation();
    const locale = muiLocaleText(i18n.language);
    const {
        elementRef,
        calculateTop
    } = CustomDateTimePickerApp({ type: DATE_TIME_PICKER_MODEL.TIME});

    const isValidTimeString = (timeString) => {
        const regex = /^([01]?[0-9]|2[0-3]):([0-5]?[0-9]):([0-5]?[0-9])$/;
        return regex.test(timeString);
    };

    const getValue = (date) => {
        if (!date) return null;
        if (formatValue) {
            return moment(date).format(inputFormat);
        } else {
            return date;
        }
    };

    const setValue = (value) => {
        if (value === undefined) return undefined;
        if (value === '' || value == null) return null;
        if (formatValue && value !== '' && typeof value === 'string' && isValidTimeString(value)) {
            return moment(value, inputFormat).toDate();
        } else {
            return value;
        }
    };

    const parsedValue = setValue(value);
    const isPmSelected = parsedValue ? moment(parsedValue).hour() >= 12 : false;

    return (
        <div ref={elementRef}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateInputLanguage(lCode)} localeText={locale}>
                <MobileTimePicker
                    id={id}
                    label={`${title}`}
                    value={setValue(value)}
                    name={name}
                    onChange={(e) => {
                        handleChange(getValue(e));
                    }}
                    required={required}
                    disabled={disabled}
                    ampm={ampm}
                    ampmInClock={ampm}
                    openTo={openTo}
                    views={views}
                    sx={((emptyFields && (value === '' || value === null)) || (!isValid && value !== '' && value !== null)) ? CustomStyleDatePickerRequired() : CustomStyleDatePicker(helperText && !emptyFields && isValid)}
                    minTime={setValue(minTime)}
                    maxTime={setValue(maxTime)}
                    onOpen={() => calculateTop(DATE_TIME_PICKER_MODEL.TIME)}
                    slotProps={{
                        textField: {
                            id: id,
                            error: emptyFields && (value === '' || value == null),
                            required: required,
                            size: 'small',
                            fullWidth: true,
                            InputProps: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <TimePickerChip width="28" height="28"/>
                                    </InputAdornment>
                                ),
                            },
                        },
                        actionBar: {
                            actions: showOkButton ? ['cancel', 'accept'] : []
                        },
                        mobilePaper: {
                            sx: CustomStyleTimePickerPopper(isPmSelected)
                        }
                    }}
                />

                {emptyFields && (value === '' || value === null) &&
                <FormHelperText error={true} sx={CustomStyleFormHelperText}>{t('requiredField')}</FormHelperText>
                }

                {!isValid && value !== '' &&
                <FormHelperText error={true} sx={CustomStyleFormHelperText}>{t(isValidText)}</FormHelperText>
                }


                { helperText && !emptyFields && isValid &&
                <FormHelperText sx={CustomStyleFormHelperText}>{t(helperText)}</FormHelperText>
                }
            </LocalizationProvider>
        </div>

    );
};

export default CustomTimePicker;
